import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from "./ui/card";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronRight, ChevronLeft, Check, User, Users, Receipt, Layout, CreditCard, Car, Briefcase, ChevronUp, ChevronDown, Trash2, X } from "lucide-react";
import axios from "axios";
import EventPopup from "./EventPopup";

// itt vannak a jegy árak, remélem jól írtam be
const TICKET_PRICES = {
  "I. kategória": 100000,
  "II. kategória": 85000,
  "III. kategória": 80000,
  "IV. kategória": 75000,
};

const BAL_MENU = [
  { 
    title: "Hideg ízelítő", 
    desc: "Kacsamáj ganache, kardamomos körte kompót bergamottal és citrom verbénával, kaláccsal",
    img: "/images/menu/hideg-izelite.jpg"
  },
  { 
    title: "Leves", 
    desc: "Jókai Tom yum, konfitált lóbab, angus nyelv, szójás csepegtetett tejföl, infuzionált zöldség brunoise, babér és paprikamag olaj, kovászos kenyér",
    img: "/images/menu/leves.jpg"
  },
  { 
    title: "Főétel", 
    desc: "Borjúszűz paprikás, vajas nokedli, szezámos uborka saláta",
    img: "/images/menu/foetel.jpg"
  },
  { 
    title: "Desszert", 
    desc: "Tonkababos csokoládé mousse feketeribizli betéttel",
    img: "/images/menu/desszert.jpg"
  }
];

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const ticketType = queryParams.get("type") || "I. kategória";

  const [step, setStep] = useState(1);
  
  useEffect(() => {
    // Redirect to home since registration is disabled
    const timer = setTimeout(() => {
      navigate("/");
    }, 5000);
    return () => clearTimeout(timer);
  }, [navigate]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    licensePlate: "",
    referralSource: "",
    participants: [{ prefix: "", firstName: "", lastName: "", menu: "Négyfogásos vacsora Pallag Dávid séftől", field: "Jog", guestType: "Teljes árú", comment: "" }],
    billing: {
      name: "",
      address: "",
      taxNumber: "",
    },
    createAccount: false,
    password: "",
  });
  
  // loading állapot a gombokhoz hogy ne kattintsanak kétszer
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [regId, setRegId] = useState("");
  const [accountInfo, setAccountInfo] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
  const API = `${BACKEND_URL}/api`;

  const handleNext = () => setStep(step + 1);
  const handleBack = () => setStep(step - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.acceptPrivacy) {
      alert("Az adatvédelmi szabályzat elfogadása kötelező!");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/tickets/register`, {
        ...formData,
        ticket_type: ticketType,
      });
      setRegId(response.data.id);
      if (response.data.account_password) {
        setAccountInfo({
          email: response.data.email,
          password: response.data.account_password
        });
      }
      setSuccess(true);
    } catch (err) {
      alert("Hiba történt a regisztráció során. Kérjük próbálja újra!");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateParticipant = (index, field, value) => {
    const newParticipants = [...formData.participants];
    newParticipants[index][field] = value;
    setFormData({ ...formData, participants: newParticipants });
  };

  const addParticipant = () => {
    setFormData({
      ...formData,
      participants: [...formData.participants, { prefix: "", firstName: "", lastName: "", menu: "Négyfogásos vacsora Pallag Dávid séftől", field: "Jog", guestType: "Teljes árú", comment: "" }]
    });
  };

  const removeParticipant = (index) => {
    if (formData.participants.length > 1) {
      const newParticipants = formData.participants.filter((_, i) => i !== index);
      setFormData({ ...formData, participants: newParticipants });
    }
  };

  const moveParticipant = (index, direction) => {
    const newParticipants = [...formData.participants];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex >= 0 && targetIndex < newParticipants.length) {
      [newParticipants[index], newParticipants[targetIndex]] = [newParticipants[targetIndex], newParticipants[index]];
      setFormData({ ...formData, participants: newParticipants });
    }
  };

  const updateBilling = (field, value) => {
    setFormData({
      ...formData,
      billing: { ...formData.billing, [field]: value }
    });
  };

  const totalAmount = formData.participants.reduce((acc, p) => {
    const basePrice = TICKET_PRICES[ticketType] || 0;
    if (p.guestType === "Díszvendég") return acc;
    if (p.guestType === "Partner") return acc + (basePrice * 0.9);
    return acc + basePrice;
  }, 0);

  const isParticipantValid = (p) => p.firstName && p.lastName;

  if (success) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6 font-serif overflow-y-auto py-20">
        <div className="max-w-2xl w-full animate-fade-in space-y-8">
          <div className="text-center">
             <div className="w-20 h-20 bg-[#d4af37]/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-[#d4af37]/30">
                <Check className="w-10 h-10 text-[#d4af37]" />
             </div>
             <h2 className="text-[#d4af37] text-4xl mb-4 uppercase tracking-[0.2em] font-black">Sikeres Regisztráció!</h2>
             <p className="text-gray-400 mb-8 font-sans text-sm">Köszönjük jelentkezését. A visszaigazoló e-mailt elküldtük a megadott címre.</p>
          </div>

          {accountInfo && (
            <Card className="bg-[#1a1a1a] border-[#22C55E]/30 rounded-none shadow-[0_0_40px_-15px_rgba(34,197,94,0.3)] mb-8">
              <CardHeader className="border-b border-white/5 bg-white/[0.02]">
                <CardTitle className="text-sm uppercase tracking-widest text-[#22C55E] flex items-center gap-2">
                  <User className="w-4 h-4" /> Fiók Létrehozva - Belépési Útmutató
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-8 space-y-6 font-sans">
                <div className="space-y-2">
                  <p className="text-gray-400 text-xs uppercase tracking-widest font-bold">Hogyan tudok belépni?</p>
                  <p className="text-[11px] text-gray-500 leading-relaxed">
                    A rendszer automatikusan létrehozta fiókját. A belépéshez használja az alábbi adatokat a főoldali <span className="text-[#d4af37] font-bold">"Belépés"</span> gombra kattintva, vagy a <span className="text-[#d4af37] font-bold">jegyek.budapestijogaszbal.com/admin</span> oldalon.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 bg-black border border-white/5">
                    <p className="text-[10px] text-gray-500 uppercase font-black mb-1">E-mail / Felhasználónév</p>
                    <p className="text-white font-bold">{accountInfo.email}</p>
                  </div>
                  <div className="p-3 bg-black border border-white/5">
                    <p className="text-[10px] text-gray-500 uppercase font-black mb-1">Jelszó</p>
                    <p className="text-white font-bold">{accountInfo.password}</p>
                  </div>
                </div>

                <div className="pt-2 space-y-4">
                  <div className="p-3 bg-[#d4af37]/5 border border-[#d4af37]/20">
                    <p className="text-[11px] text-[#d4af37] font-bold mb-1 ml-1 uppercase tracking-tight">Mit tud a fiókban kezelni?</p>
                    <ul className="text-[10px] text-gray-400 space-y-1 list-disc list-inside">
                      <li>Résztvevők nevének és sorrendjének módosítása</li>
                      <li>Számlázási adatok frissítése</li>
                      <li>Regisztráció kifizetettségének ellenőrzése</li>
                    </ul>
                  </div>
                  
                  <Button 
                    className="w-full bg-[#22C55E] text-black font-black uppercase tracking-widest py-4 rounded-none hover:bg-white transition-all text-xs shadow-[0_5px_20px_-5px_rgba(34,197,94,0.4)]"
                    onClick={() => navigate("/admin")}
                  >
                    Belépés a Jegykezelőbe
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="bg-[#111] border-[#d4af37]/30 rounded-none shadow-[0_0_40px_-15px_rgba(212,175,55,0.3)]">
            <CardHeader className="border-b border-white/5 bg-white/[0.02]">
              <CardTitle className="text-sm uppercase tracking-widest text-[#d4af37] flex items-center gap-2">
                <CreditCard className="w-4 h-4" /> Átutalási adatok
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-1">
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Kedvezményezett</p>
                  <p className="text-white font-sans text-lg">Bowman and Becker Kft.</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Bank</p>
                  <p className="text-white font-sans text-lg">MBH Bank</p>
                </div>
                <div className="md:col-span-2 space-y-1 relative group">
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Számlaszám</p>
                  <div className="flex items-center gap-3">
                    <p className="text-[#d4af37] font-sans text-xl font-bold tracking-wider">11600006-00000002-04040518</p>
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] uppercase tracking-widest text-[#d4af37] font-bold">Közlemény (Regisztrációs kód)</p>
                  <p className="text-white font-sans text-2xl font-black tracking-widest uppercase">{regId}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Fizetendő összeg</p>
                  <p className="text-white font-sans text-2xl font-black">{totalAmount.toLocaleString()} Ft</p>
                </div>
              </div>
            </CardContent>
            <CardFooter className="bg-white/[0.02] border-t border-white/5 py-6">
               <p className="text-gray-500 font-sans text-xs italic">
                 Kérjük, az utalás során a közlemény rovatban tüntesse fel a fenti regisztrációs kódot a könnyebb beazonosítás érdekében.
               </p>
            </CardFooter>
          </Card>

          <div className="flex justify-center pt-8">
            <Button 
              className="bg-white text-black font-bold uppercase py-6 px-12 rounded-none hover:bg-[#d4af37] hover:text-black transition-all"
              onClick={() => navigate("/")}
            >
              Vissza a főoldalra
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const steps = [
    { id: 1, name: "Kapcsolattartó", icon: <User className="w-4 h-4" /> },
    { id: 2, name: "Résztvevők", icon: <Users className="w-4 h-4" /> },
    { id: 3, name: "Számlázás", icon: <Receipt className="w-4 h-4" /> },
    { id: 4, name: "Összegzés", icon: <Layout className="w-4 h-4" /> },
  ];

  const SelectStyled = ({ children, ...props }) => (
    <div className="relative group">
       <select 
         {...props}
         className="w-full bg-black border-b border-white/20 py-3 appearance-none focus:outline-none focus:border-[#d4af37] transition-all font-sans text-white text-sm cursor-pointer hover:border-white/40"
       >
         {children}
       </select>
       <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 group-hover:text-[#d4af37] transition-colors">
          <ChevronDown className="w-4 h-4" />
       </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white font-serif selection:bg-[#d4af37] selection:text-black p-6 md:p-12 pb-32">
      <EventPopup />
      <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-6 text-center">
        <div className="max-w-md space-y-6">
          <h2 className="text-[#d4af37] text-3xl font-serif uppercase tracking-widest">A regisztráció jelenleg szünetel</h2>
          <p className="text-gray-400 font-sans">Az esemény új időpontja miatt a jegyértékesítést ideiglenesen felfüggesztettük. Kérjük, tekintse meg a főoldalon található tájékoztatót.</p>
          <Button onClick={() => navigate("/")} className="bg-[#d4af37] text-black font-bold uppercase py-6 px-12 rounded-none">Vissza a főoldalra</Button>
          <p className="text-[10px] text-gray-600 uppercase tracking-widest mt-4">Automatikus átirányítás 5 másodperc múlva...</p>
        </div>
      </div>
      <nav className="mb-12 flex justify-between items-center">
        <button onClick={() => navigate("/")} className="text-[#d4af37] uppercase tracking-widest text-xs font-bold hover:text-white transition-colors flex items-center gap-2 text-shadow-glow">
          ← Vissza
        </button>
        <div className="text-[#d4af37] font-bold tracking-widest uppercase text-sm border-b border-[#d4af37]/30 pb-1">
          {ticketType}
        </div>
      </nav>

      {/* STEP INDICATOR */}
      <div className="max-w-4xl mx-auto mb-16">
        <div className="flex justify-between items-center relative">
            <div className="absolute top-1/2 left-0 w-full h-0.5 bg-white/10 -translate-y-1/2 -z-10"></div>
            {steps.map((s) => (
                <div key={s.id} className="flex flex-col items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${step >= s.id ? 'bg-[#d4af37] border-[#d4af37] text-black shadow-[0_0_15px_rgba(212,175,55,0.4)]' : 'bg-black border-white/20 text-gray-500'}`}>
                        {step > s.id ? <Check className="w-5 h-5 stroke-[3px]" /> : s.icon}
                    </div>
                    <span className={`text-[9px] uppercase tracking-[0.2em] font-black ${step >= s.id ? 'text-[#d4af37]' : 'text-gray-500'}`}>{s.name}</span>
                </div>
            ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto">
        <div className="bg-[#0a0a0a] border border-white/5 p-8 md:p-12 shadow-[0_0_100px_-20px_rgba(0,0,0,1)] relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-[#d4af37]"></div>
          
          {/* STEP 1: CONTACT */}
          {step === 1 && (
            <div className="animate-fade-in space-y-8">
              <div>
                <h2 className="text-3xl font-black uppercase mb-2">Kapcsolattartó <span className="text-[#d4af37]">Adatai</span></h2>
                <p className="text-gray-500 font-sans text-sm tracking-tight">Adja meg az elsődleges kapcsolattartó adatait az ügyintézéshez.</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <label className="block text-[10px] uppercase tracking-[0.3em] font-bold text-gray-500">Teljes Név</label>
                  <input 
                    required
                    type="text"
                    className="w-full bg-transparent border-b border-white/20 py-3 focus:outline-none focus:border-[#d4af37] transition-colors font-sans text-lg"
                    placeholder="Pl. Dr. Kovács János"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </div>
                <div className="space-y-4">
                  <label className="block text-[10px] uppercase tracking-[0.3em] font-bold text-gray-500">E-mail Cím</label>
                  <input 
                    required
                    type="email"
                    className="w-full bg-transparent border-b border-white/20 py-3 focus:outline-none focus:border-[#d4af37] transition-colors font-sans text-lg"
                    placeholder="kovacs.janos@example.hu"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>
                <div className="space-y-4">
                  <label className="block text-[10px] uppercase tracking-[0.3em] font-bold text-gray-500">Telefonszám</label>
                  <input 
                    required
                    type="tel"
                    className="w-full bg-transparent border-b border-white/20 py-3 focus:outline-none focus:border-[#d4af37] transition-colors font-sans text-lg"
                    placeholder="+36 30 123 4567"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </div>
                <div className="space-y-4">
                  <label className="block text-[10px] uppercase tracking-[0.3em] font-bold text-gray-500">Honnan hallott a bálról?</label>
                  <input 
                    type="text"
                    className="w-full bg-transparent border-b border-white/20 py-3 focus:outline-none focus:border-[#d4af37] transition-colors font-sans text-lg"
                    placeholder="Pl. Ismerős, Facebook, Hírlevél"
                    value={formData.referralSource}
                    onChange={(e) => setFormData({...formData, referralSource: e.target.value})}
                  />
                </div>
                <div className="space-y-4">
                  <label className="block text-[10px] uppercase tracking-[0.3em] font-bold text-gray-500 flex items-center gap-2">
                    <Car className="w-3 h-3" /> Rendszám (Parkoláshoz)*
                  </label>
                  <input 
                    type="text"
                    className="w-full bg-transparent border-b border-white/20 py-3 focus:outline-none focus:border-[#d4af37] transition-colors font-sans text-lg"
                    placeholder="ABC-123"
                    value={formData.licensePlate}
                    onChange={(e) => setFormData({...formData, licensePlate: e.target.value})}
                  />
                  <p className="text-[10px] text-gray-500 italic font-sans leading-relaxed">
                    *A férőhelyek korlátozott számban állnak rendelkezésre, és a befizetések beérkezési sorrendjében kerülnek lefoglalásra.
                  </p>
                </div>
              </div>

              <div className="pt-8 flex justify-end">
                <Button onClick={handleNext} disabled={!formData.name || !formData.email || !formData.phone} className="bg-[#d4af37] text-black font-bold uppercase py-8 px-12 rounded-none hover:bg-white transition-all flex items-center gap-2 shadow-[0_10px_30px_-10px_rgba(212,175,55,0.3)]">
                  Folytatás <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* STEP 2: PARTICIPANTS */}
          {step === 2 && (
            <div className="animate-fade-in space-y-8">
              <div>
                <h2 className="text-3xl font-black uppercase mb-2">Résztvevők és <span className="text-[#d4af37]">Menü</span></h2>
                <div className="p-3 bg-[#d4af37]/10 border-l-2 border-[#d4af37] mb-6">
                   <p className="text-[#d4af37] font-sans text-xs font-bold uppercase tracking-widest leading-relaxed">
                     Fontos: Kérjük, a résztvevőket az ültetési sorrendnek megfelelően rögzítse.
                   </p>
                </div>
              </div>

              {/* BÁL MENÜ DISPLAY */}
              <div className="p-6 border border-[#d4af37]/20 bg-[#d4af37]/5 space-y-4 mb-4">
                 <h3 className="text-[#d4af37] text-xs uppercase tracking-widest font-bold border-b border-white/10 pb-2">Ünnepi Menüsor</h3>
                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {BAL_MENU.map((item, idx) => (
                      <div key={idx} className="group cursor-zoom-in" onClick={() => setLightboxImage(item)}>
                        <div className="aspect-square relative overflow-hidden border border-white/10 mb-2">
                          <img src={item.img} alt={item.title} className="w-full h-full object-cover transition-transform group-hover:scale-110" />
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center p-2 text-center">
                            <p className="text-[10px] leading-relaxed text-white font-sans px-2">{item.desc}</p>
                          </div>
                        </div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#d4af37] text-center">{item.title}</p>
                      </div>
                    ))}
                 </div>
              </div>

              <div className="space-y-12">
                {formData.participants.map((p, index) => (
                  <div key={index} className="relative p-6 border border-white/5 bg-white/[0.02] group/participant">
                    <div className="absolute -top-3 left-6 bg-black px-3 text-[10px] uppercase tracking-widest font-black text-[#d4af37] border border-white/10">
                      {index + 1}. Résztvevő
                    </div>
                    
                    <div className="absolute top-4 right-4 flex items-center gap-4">
                      <div className="flex flex-col gap-1">
                         <button disabled={index === 0} onClick={() => moveParticipant(index, 'up')} className="p-1 hover:text-[#d4af37] disabled:opacity-20 transition-colors"><ChevronUp className="w-4 h-4" /></button>
                         <button disabled={index === formData.participants.length - 1} onClick={() => moveParticipant(index, 'down')} className="p-1 hover:text-[#d4af37] disabled:opacity-20 transition-colors"><ChevronDown className="w-4 h-4" /></button>
                      </div>
                      {formData.participants.length > 1 && (
                        <button onClick={() => removeParticipant(index)} className="text-red-500/50 hover:text-red-500 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
                      <div className="space-y-4">
                        <label className="block text-[9px] uppercase tracking-[0.3em] font-bold text-gray-500">Előtag</label>
                        <input 
                          type="text"
                          className="w-full bg-transparent border-b border-white/20 py-2 focus:outline-none focus:border-[#d4af37] transition-colors font-sans text-sm"
                          placeholder="Dr. / Id."
                          value={p.prefix}
                          onChange={(e) => updateParticipant(index, 'prefix', e.target.value)}
                        />
                      </div>
                      <div className="space-y-4">
                        <label className="block text-[9px] uppercase tracking-[0.3em] font-bold text-gray-500">Vezetéknév</label>
                        <input 
                          required
                          type="text"
                          className="w-full bg-transparent border-b border-white/20 py-2 focus:outline-none focus:border-[#d4af37] transition-colors font-sans text-sm"
                          placeholder="Kovács"
                          value={p.lastName}
                          onChange={(e) => updateParticipant(index, 'lastName', e.target.value)}
                        />
                      </div>
                      <div className="space-y-4">
                        <label className="block text-[9px] uppercase tracking-[0.3em] font-bold text-gray-500">Keresztnév</label>
                        <input 
                          required
                          type="text"
                          className="w-full bg-transparent border-b border-white/20 py-2 focus:outline-none focus:border-[#d4af37] transition-colors font-sans text-sm"
                          placeholder="János"
                          value={p.firstName}
                          onChange={(e) => updateParticipant(index, 'firstName', e.target.value)}
                        />
                      </div>

                      <div className="space-y-4">
                        <label className="block text-[9px] uppercase tracking-[0.3em] font-bold text-gray-500 flex items-center gap-2">
                          <Briefcase className="w-3 h-3" /> Szektor
                        </label>
                        <SelectStyled 
                          value={p.field}
                          onChange={(e) => updateParticipant(index, 'field', e.target.value)}
                        >
                          <option value="Jog">Jog</option>
                          <option value="Gazdaság">Gazdaság</option>
                          <option value="Egyéb">Egyéb</option>
                        </SelectStyled>
                      </div>

                      <div className="space-y-4 md:col-span-2">
                        <label className="block text-[9px] uppercase tracking-[0.3em] font-bold text-gray-500 mb-4">Választott Menü</label>
                        <div className="flex items-center gap-3 p-3 border border-[#d4af37]/30 bg-[#d4af37]/5">
                           <input 
                             type="radio" 
                             checked={p.menu === "Négyfogásos vacsora Pallag Dávid séftől"} 
                             onChange={() => updateParticipant(index, 'menu', "Négyfogásos vacsora Pallag Dávid séftől")}
                             className="accent-[#d4af37] w-4 h-4"
                           />
                           <div>
                             <p className="text-[11px] font-bold uppercase tracking-wider text-white">Négyfogásos vacsora Pallag Dávid séftől</p>
                             <p className="text-[9px] text-gray-500 font-sans mt-0.5">Szezonális válogatás (Vegetáriánus opció a megjegyzésben kérhető)</p>
                           </div>
                        </div>
                      </div>

                      <div className="md:col-span-2 space-y-4">
                        <label className="block text-[9px] uppercase tracking-[0.3em] font-bold text-gray-500">Megjegyzés / Allergia</label>
                        <input 
                          type="text"
                          className="w-full bg-transparent border-b border-white/20 py-2 focus:outline-none focus:border-[#d4af37] transition-colors font-sans text-sm"
                          placeholder="Speciális kérés vagy allergia"
                          value={p.comment}
                          onChange={(e) => updateParticipant(index, 'comment', e.target.value)}
                        />
                      </div>

                      <div className="md:col-span-3 pt-4 border-t border-white/5">
                        <label className="block text-[9px] uppercase tracking-[0.3em] font-bold text-gray-500 mb-4 text-center">Résztvevő típusa</label>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                           {[
                             { id: "Teljes árú", label: "Fizető vendég", desc: "Teljes árú jegy", discount: "" },
                             { id: "Díszvendég", label: "Díszvendég", desc: "Ingyenes részvétel", discount: "FREE" },
                             { id: "Partner", label: "Partner", desc: "10% kedvezmény", discount: "-10%" }
                           ].map((type) => (
                             <div 
                               key={type.id}
                               onClick={() => updateParticipant(index, 'guestType', type.id)}
                               className={`cursor-pointer p-4 border transition-all flex flex-col items-center text-center gap-1 ${
                                 p.guestType === type.id 
                                   ? 'bg-[#d4af37]/10 border-[#d4af37] shadow-[0_0_20px_rgba(212,175,55,0.1)]' 
                                   : 'bg-white/[0.02] border-white/10 hover:border-white/20'
                               }`}
                             >
                                <div className="flex items-center gap-2">
                                  <span className={`text-[11px] font-black uppercase tracking-widest ${p.guestType === type.id ? 'text-[#d4af37]' : 'text-white'}`}>{type.label}</span>
                                  {type.discount && <span className="bg-[#d4af37] text-black text-[8px] px-1 font-bold">{type.discount}</span>}
                                </div>
                                <span className="text-[9px] text-gray-500 font-sans">{type.desc}</span>
                             </div>
                           ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                <button 
                  onClick={addParticipant}
                  className="w-full border-2 border-dashed border-white/10 py-8 text-gray-500 hover:text-[#d4af37] hover:border-[#d4af37]/40 transition-all font-black uppercase text-[10px] tracking-[0.3em] hover:bg-[#d4af37]/5 group"
                >
                  <span className="group-hover:scale-110 transition-transform inline-block">+ Új résztvevő hozzáadása</span>
                </button>
              </div>

              <div className="pt-8 flex justify-between">
                <Button onClick={handleBack} variant="outline" className="border-white/10 text-white font-bold uppercase py-8 px-12 rounded-none hover:bg-white/5 transition-all flex items-center gap-2">
                  <ChevronLeft className="w-4 h-4" /> Vissza
                </Button>
                <Button onClick={handleNext} disabled={formData.participants.some(p => !isParticipantValid(p))} className="bg-[#d4af37] text-black font-bold uppercase py-8 px-12 rounded-none hover:bg-white transition-all flex items-center gap-2 shadow-[0_10px_30px_-10px_rgba(212,175,55,0.3)]">
                  Folytatás <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* STEP 3: BILLING */}
          {step === 3 && (
            <div className="animate-fade-in space-y-8">
              <div>
                <h2 className="text-3xl font-black uppercase mb-2">Számlázási <span className="text-[#d4af37]">Adatok</span></h2>
                <p className="text-gray-500 font-sans text-sm">Kérjük, adja meg a számla kiállításához szükséges adatokat.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="md:col-span-2 space-y-4">
                  <label className="block text-[10px] uppercase tracking-[0.3em] font-bold text-gray-500">Számlázási Név / Cégnév</label>
                  <input 
                    required
                    type="text"
                    className="w-full bg-transparent border-b border-white/20 py-3 focus:outline-none focus:border-[#d4af37] transition-colors font-sans text-lg"
                    placeholder="Pl. Dr. Kovács János vagy Kovács és Társai Kft."
                    value={formData.billing.name}
                    onChange={(e) => updateBilling('name', e.target.value)}
                  />
                </div>
                <div className="md:col-span-2 space-y-4">
                  <label className="block text-[10px] uppercase tracking-[0.3em] font-bold text-gray-500">Számlázási Cím</label>
                  <input 
                    required
                    type="text"
                    className="w-full bg-transparent border-b border-white/20 py-3 focus:outline-none focus:border-[#d4af37] transition-colors font-sans text-lg"
                    placeholder="Város, Utca, Házszám, Irányítószám"
                    value={formData.billing.address}
                    onChange={(e) => updateBilling('address', e.target.value)}
                  />
                </div>
                <div className="space-y-4">
                  <label className="block text-[10px] uppercase tracking-[0.3em] font-bold text-gray-500">Adószám (Cégek esetén)</label>
                  <input 
                    type="text"
                    className="w-full bg-transparent border-b border-white/20 py-3 focus:outline-none focus:border-[#d4af37] transition-colors font-sans text-lg"
                    placeholder="12345678-1-12"
                    value={formData.billing.taxNumber}
                    onChange={(e) => updateBilling('taxNumber', e.target.value)}
                  />
                </div>
              </div>

              <div className="pt-8 flex justify-between">
                <Button onClick={handleBack} variant="outline" className="border-white/10 text-white font-bold uppercase py-8 px-12 rounded-none hover:bg-white/5 transition-all flex items-center gap-2">
                  <ChevronLeft className="w-4 h-4" /> Vissza
                </Button>
                <Button onClick={handleNext} disabled={!formData.billing.name || !formData.billing.address} className="bg-[#d4af37] text-black font-bold uppercase py-8 px-12 rounded-none hover:bg-white transition-all flex items-center gap-2 shadow-[0_10px_30px_-10px_rgba(212,175,55,0.3)]">
                  Folytatás <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* STEP 4: SUMMARY */}
          {step === 4 && (
            <div className="animate-fade-in space-y-8">
              <div>
                <h2 className="text-3xl font-black uppercase mb-2">Összegzés és <span className="text-[#d4af37]">Befejezés</span></h2>
                <p className="text-gray-500 font-sans text-sm">Ellenőrizze az adatokat és erősítse meg a regisztrációt.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="p-6 border border-white/5 bg-white/[0.02] space-y-4">
                  <h3 className="text-[#d4af37] text-xs uppercase tracking-widest font-bold border-b border-white/10 pb-2">Kapcsolattartó</h3>
                  <div className="space-y-1">
                    <p className="text-white text-lg font-sans">{formData.name}</p>
                    <p className="text-gray-500 font-sans text-sm">{formData.email}</p>
                    <p className="text-gray-500 font-sans text-sm">{formData.phone}</p>
                    <div className="flex flex-wrap gap-4 mt-2">
                        {formData.licensePlate && <p className="text-gray-400 font-sans text-xs flex items-center gap-2"><Car className="w-3 h-3 text-[#d4af37]" /> {formData.licensePlate}</p>}
                        {formData.referralSource && <p className="text-gray-400 font-sans text-xs flex items-center gap-2"><Users className="w-3 h-3 text-[#d4af37]" /> {formData.referralSource}</p>}
                    </div>
                  </div>
                </div>

                <div className="p-6 border border-white/5 bg-white/[0.02] space-y-4">
                  <h3 className="text-[#d4af37] text-xs uppercase tracking-widest font-bold border-b border-white/10 pb-2">Számlázási Adatok</h3>
                  <div className="space-y-1">
                    <p className="text-white text-lg font-sans">{formData.billing.name}</p>
                    <p className="text-gray-500 font-sans text-sm">{formData.billing.address}</p>
                    {formData.billing.taxNumber && <p className="text-gray-500 font-sans text-sm">Adószám: {formData.billing.taxNumber}</p>}
                  </div>
                </div>

                <div className="md:col-span-2 p-6 border border-white/5 bg-white/[0.02] space-y-4">
                  <h3 className="text-[#d4af37] text-xs uppercase tracking-widest font-bold border-b border-white/10 pb-2">Résztvevők ({formData.participants.length} fő)</h3>
                  <div className="space-y-4">
                    {formData.participants.map((p, i) => (
                      <div key={i} className="flex justify-between items-start border-b border-white/5 pb-4 last:border-0 last:pb-0">
                        <div className="flex-1">
                          <p className="text-white font-sans font-medium">
                            {p.prefix} {p.lastName} {p.firstName} 
                            {p.guestType === "Díszvendég" && <span className="text-[9px] bg-[#d4af37] text-black px-1.5 py-0.5 ml-2 font-black uppercase tracking-tighter">Díszvendég (Ingyenes)</span>}
                            {p.guestType === "Partner" && <span className="text-[9px] bg-[#d4af37]/30 text-[#d4af37] border border-[#d4af37]/30 px-1.5 py-0.5 ml-2 font-black uppercase tracking-tighter">Partner (10% Kedv.)</span>}
                          </p>
                          <div className="flex gap-4 mt-1.5">
                            <span className="text-[10px] uppercase font-bold text-gray-500 flex items-center gap-1"><Briefcase className="w-2.5 h-2.5" /> {p.field}</span>
                            <span className="text-[10px] uppercase font-bold text-[#d4af37]">{p.menu}</span>
                          </div>
                          {p.comment && <p className="text-gray-500 text-xs font-sans italic mt-1 font-medium">"{p.comment}"</p>}
                        </div>
                        <div className="text-[10px] text-gray-600 font-black">#{i+1}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-6 border border-[#d4af37]/40 bg-[#d4af37]/5 flex justify-between items-center shadow-[inset_0_0_50px_rgba(212,175,55,0.05)]">
                  <div className="flex flex-col">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-black text-[#d4af37]">Összesen fizetendő</span>
                    <span className="text-gray-500 text-[10px] font-sans">Utalással történő teljesítés</span>
                  </div>
                  <span className="text-3xl font-black text-white">br. {totalAmount.toLocaleString()} Ft</span>
              </div>

              <div className="border border-white/5 p-6 bg-white/[0.01] space-y-6">
                <div className="flex items-center gap-3">
                  <input 
                    type="checkbox"
                    id="createAccount"
                    className="w-4 h-4 accent-[#d4af37]"
                    checked={formData.createAccount}
                    onChange={(e) => setFormData({...formData, createAccount: e.target.checked})}
                  />
                  <label htmlFor="createAccount" className="text-xs uppercase tracking-widest font-bold cursor-pointer text-gray-400 hover:text-white transition-colors">Szeretnék fiókot létrehozni a jövőbeni jegykezeléshez</label>
                </div>
                
                {formData.createAccount && (
                  <div className="animate-fade-in space-y-4 pt-2">
                    <label className="block text-[10px] uppercase tracking-[0.3em] font-bold text-gray-500">Jelszó kiválasztása</label>
                    <input 
                      required={formData.createAccount}
                      type="password"
                      className="w-full bg-transparent border-b border-white/20 py-3 focus:outline-none focus:border-[#d4af37] transition-colors font-sans"
                      placeholder="Minimum 8 karakter"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                    />
                  </div>
                )}
              </div>

              <div className="border border-white/5 p-6 bg-white/[0.01] flex items-center gap-3">
                <input 
                  type="checkbox"
                  id="acceptPrivacy"
                  className="w-4 h-4 accent-[#d4af37]"
                  checked={formData.acceptPrivacy}
                  onChange={(e) => setFormData({...formData, acceptPrivacy: e.target.checked})}
                />
                <label htmlFor="acceptPrivacy" className="text-[10px] uppercase tracking-widest font-bold cursor-pointer text-gray-400 hover:text-white transition-colors">
                  Elfogadom az <a href="/adatvedelem" target="_blank" className="text-[#d4af37] underline">adatvédelmi szabályzatot</a>
                </label>
              </div>

              <div className="pt-8 flex justify-between">
                <Button onClick={handleBack} variant="outline" className="border-white/10 text-white font-bold uppercase py-8 px-12 rounded-none hover:bg-white/5 transition-all flex items-center gap-2">
                  <ChevronLeft className="w-4 h-4" /> Vissza
                </Button>
                <Button 
                  onClick={handleSubmit} 
                  disabled={loading || (formData.createAccount && formData.password.length < 6)} 
                  className="bg-[#d4af37] text-black font-bold uppercase py-8 px-12 rounded-none hover:bg-white transition-all flex items-center gap-2 shadow-[0_0_60px_-15px_rgba(212,175,55,0.6)]"
                >
                  {loading ? "Feldolgozás..." : "Regisztráció véglegesítése"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* FOOTER SNIPPET */}
      {/* ide raktam a láblécet, reméllem eléggé elegáns let */}
      <footer className="max-w-4xl mx-auto py-4 mt-12 sm:mt-24 border-t border-white/5 flex justify-center items-center">
        <div className="flex items-center gap-2">
          <span className="text-white/50 font-['Arial',sans-serif] text-[12px] mr-1">made with love by:</span>
          <a href="https://www.devhound.hu/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <span className="text-white/90 font-['Arial',sans-serif] font-medium text-[14px]">Bowman and Becker</span>
            <div className="w-px h-4 bg-white/20 mx-1"></div>
            <div className="bg-[#111111] border border-white/5 px-2.5 py-1 rounded flex items-center justify-center">
              <span className="text-[#22C55E] font-mono text-[12px] font-bold tracking-wide">&lt;/CODE&gt;</span>
            </div>
          </a>
        </div>
      </footer>

      <style>{`
        @keyframes fade-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fade-in 0.5s ease-out forwards; }
        .text-shadow-glow { text-shadow: 0 0 10px rgba(212,175,55,0.3); }
      `}</style>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4 md:p-12 animate-fade-in"
          onClick={() => setLightboxImage(null)}
        >
           <button className="absolute top-6 right-6 text-white/50 hover:text-[#d4af37] transition-colors p-4">
             <X size={32} />
           </button>
           
           <div className="w-full max-w-5xl space-y-8" onClick={(e) => e.stopPropagation()}>
             <div className="relative group">
                <div className="absolute -inset-1 bg-[#d4af37]/20 blur-2xl opacity-50"></div>
                <img 
                  src={lightboxImage.img} 
                  alt={lightboxImage.title} 
                  className="relative w-full aspect-[4/3] md:aspect-video object-cover border border-white/10 shadow-2xl"
                />
             </div>
             
             <div className="text-center space-y-4 max-w-2xl mx-auto">
                <h3 className="text-[#d4af37] text-4xl font-black uppercase tracking-[0.2em]">{lightboxImage.title}</h3>
                <div className="w-12 h-0.5 bg-[#d4af37] mx-auto opacity-50"></div>
                <p className="text-white text-lg font-sans leading-relaxed tracking-tight">{lightboxImage.desc}</p>
                <div className="pt-4">
                  <Button 
                    className="bg-transparent border border-white/20 text-white/50 hover:border-[#d4af37] hover:text-[#d4af37] rounded-none px-8 py-6 font-bold uppercase tracking-widest text-xs"
                    onClick={() => setLightboxImage(null)}
                  >
                    Bezárás
                  </Button>
                </div>
             </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
